import { KeyboardEvent, useRef, useState } from 'react'
import { markKeyboardModality } from '@/utils/focusModality'

const OPTION_SELECTOR = '[role="option"]:not(:disabled)'
const TYPEAHEAD_RESET_MS = 500

/**
 * Roving tabindex for a list of `role="option"` items: the list is a single
 * Tab stop, Arrow Up/Down, Home and End move focus between options, and
 * typing jumps to the next option whose label starts with the typed text.
 */
export function useRovingFocus (values: string[], preferred?: string | null) {
	const [focused, setFocused] = useState<string>()
	const listRef = useRef<HTMLDivElement>(null)
	const typeahead = useRef({ query: '', timer: undefined as ReturnType<typeof setTimeout> | undefined })

	const active =
		focused !== undefined && values.includes(focused)
			? focused
			: preferred != null && values.includes(preferred)
			? preferred
			: values[0]

	const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
		const list = listRef.current
		if (!list) return

		const items = Array.from(list.querySelectorAll<HTMLElement>(OPTION_SELECTOR))
		const current = items.indexOf(document.activeElement as HTMLElement)

		let next: number
		switch (e.key) {
			case 'ArrowDown':
				next = Math.min(current + 1, items.length - 1)
				break
			case 'ArrowUp':
				next = Math.max(current - 1, 0)
				break
			case 'Home':
				next = 0
				break
			case 'End':
				next = items.length - 1
				break
			default: {
				// Space stays reserved for toggling the focused option.
				if (e.key.length !== 1 || e.key === ' ' || e.ctrlKey || e.metaKey || e.altKey) return

				const state = typeahead.current
				clearTimeout(state.timer)
				state.query += e.key.toLowerCase()
				state.timer = setTimeout(() => { state.query = '' }, TYPEAHEAD_RESET_MS)

				// Repeating one character cycles through its matches; a longer query refines in place.
				const q = state.query
				const repeated = [...q].every(c => c === q[0])
				const needle = repeated ? q[0] : q
				const start = repeated ? current + 1 : Math.max(current, 0)
				const ordered = [...items.slice(start), ...items.slice(0, start)]
				const match = ordered.find(item =>
					(item.textContent ?? '').trim().toLowerCase().startsWith(needle)
				)
				if (!match) return
				next = items.indexOf(match)
			}
		}

		e.preventDefault()
		// Arrow keys move focus here, so the moved-to option needs a visible ring.
		markKeyboardModality()
		items[next]?.focus()
	}

	const getItemProps = (value: string) => ({
		tabIndex: value === active ? 0 : -1,
		onFocus: () => { setFocused(value) }
	})

	return { listRef, onKeyDown, getItemProps }
}
